import { Checkbox, cn, Input, InputGroup, InputGroupAddon, InputGroupInput, InputGroupText, Label } from '@1trading/react';
import { type ComponentProps, type ReactNode, useId, useState } from 'react';
import { ContextTooltip } from '@/components/ui/context-tooltip';
import { DatePicker } from '@/components/ui/date-picker';
import { FieldError } from '@/components/ui/field-error';
import { toUtcInstantIso } from '@/utils/date';
import { formatNumberList, parseNumberList } from './mm-program-editing';

interface FieldLabelProps {
  htmlFor: string;
  label: string;
  /** What the field does, shown on hover or focus of the label */
  description?: string;
  className?: string;
}

const FieldLabel = ({ htmlFor, label, description, className }: FieldLabelProps) =>
  description ? (
    <ContextTooltip className={className} description={description} htmlFor={htmlFor}>
      {label}
    </ContextTooltip>
  ) : (
    <Label className={cn('text-sm', className)} htmlFor={htmlFor}>
      {label}
    </Label>
  );
FieldLabel.displayName = 'FieldLabel';

interface FieldShellProps {
  id: string;
  label: string;
  description?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

const FieldShell = ({ id, label, description, hint, error, children }: FieldShellProps) => (
  <div className="flex min-w-0 flex-col gap-1.5">
    <FieldLabel description={description} htmlFor={id} label={label} />
    {children}
    {error ? <FieldError message={error} /> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);
FieldShell.displayName = 'FieldShell';

const UnitInput = ({ unit, ...props }: ComponentProps<typeof Input> & { unit?: string }) =>
  unit ? (
    <InputGroup>
      <InputGroupInput className="tabular-nums" {...props} />
      <InputGroupAddon align="inline-end">
        <InputGroupText>{unit}</InputGroupText>
      </InputGroupAddon>
    </InputGroup>
  ) : (
    <Input {...props} />
  );
UnitInput.displayName = 'UnitInput';

/** Shows `stored × scale` and stores `typed ÷ scale`; trims float noise such as 0.07 × 100. */
const toShown = (value: number | null | undefined, scale: number) =>
  value === undefined || value === null || Number.isNaN(value) ? '' : String(Number((value * scale).toPrecision(12)));

/** Blank is undefined; anything else that is not a number is NaN, which the schema reports. */
const parseNumber = (text: string, scale: number) => (text.trim() ? Number(text) / scale : undefined);

interface NumberFieldProps {
  label: string;
  value: number | null | undefined;
  onChange: (value: number | undefined) => void;
  /** 100 to edit a fraction as a percentage */
  scale?: number;
  unit?: string;
  description?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
}

export const NumberField = ({ label, value, onChange, scale = 1, unit, description, hint, error, disabled }: NumberFieldProps) => {
  const id = useId();
  // The typed text while focused, so partial input such as "1." or "-" survives the round trip through a number.
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <FieldShell description={description} error={error} hint={hint} id={id} label={label}>
      <UnitInput
        aria-invalid={Boolean(error)}
        disabled={disabled}
        id={id}
        inputMode="decimal"
        onBlur={() => setTyped(null)}
        onChange={(event) => {
          setTyped(event.target.value);
          onChange(parseNumber(event.target.value, scale));
        }}
        type="text"
        unit={unit}
        value={typed ?? toShown(value, scale)}
      />
    </FieldShell>
  );
};
NumberField.displayName = 'NumberField';

interface NumberListFieldProps {
  label: string;
  value: number[] | undefined;
  onChange: (value: number[]) => void;
  unit?: string;
  description?: string;
  hint?: string;
  error?: string;
}

/** A `;`-separated list such as volatility rungs. */
export const NumberListField = ({ label, value, onChange, unit, description, hint, error }: NumberListFieldProps) => {
  const id = useId();
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <FieldShell description={description} error={error} hint={hint} id={id} label={label}>
      <UnitInput
        aria-invalid={Boolean(error)}
        id={id}
        onBlur={() => setTyped(null)}
        onChange={(event) => {
          setTyped(event.target.value);
          onChange(parseNumberList(event.target.value));
        }}
        placeholder="10;15;20"
        type="text"
        unit={unit}
        value={typed ?? formatNumberList(value ?? [])}
      />
    </FieldShell>
  );
};
NumberListField.displayName = 'NumberListField';

interface TextListFieldProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  description?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
}

/** A comma-separated list such as BCC addresses. */
export const TextListField = ({ label, value, onChange, description, hint, error, placeholder }: TextListFieldProps) => {
  const id = useId();
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <FieldShell description={description} error={error} hint={hint} id={id} label={label}>
      <Input
        aria-invalid={Boolean(error)}
        id={id}
        onBlur={() => setTyped(null)}
        onChange={(event) => {
          setTyped(event.target.value);
          onChange(
            event.target.value
              .split(',')
              .map((part) => part.trim())
              .filter(Boolean),
          );
        }}
        placeholder={placeholder}
        type="text"
        value={typed ?? value.join(', ')}
      />
    </FieldShell>
  );
};
TextListField.displayName = 'TextListField';

interface TextFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  description?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
  readOnly?: boolean;
}

export const TextField = ({ label, value, onChange, description, hint, error, placeholder, readOnly }: TextFieldProps) => {
  const id = useId();
  return (
    <FieldShell description={description} error={error} hint={hint} id={id} label={label}>
      <Input
        aria-invalid={Boolean(error)}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        type="text"
        value={value ?? ''}
      />
    </FieldShell>
  );
};
TextField.displayName = 'TextField';

interface DateFieldProps {
  label: string;
  /** Stored program date: blank or ISO UTC; with `asDay`, blank or `YYYY-MM-DD` */
  value: string;
  onChange: (value: string) => void;
  description?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
  /** Lets the date be blanked, with a button labelled with this */
  clearLabel?: string;
  /** First and last `YYYY-MM-DD` that may be picked */
  min?: string;
  max?: string;
  /** Stores the bare `YYYY-MM-DD` day, as downtime does, rather than a program's ISO midnight */
  asDay?: boolean;
}

/** Edits the calendar day; a stored time of day is kept while the day is unchanged. */
export const DateField = ({ label, value, onChange, description, hint, error, placeholder, clearLabel, min, max, asDay }: DateFieldProps) => {
  const id = useId();
  return (
    <FieldShell description={description} error={error} hint={hint} id={id} label={label}>
      <DatePicker
        aria-invalid={Boolean(error)}
        clearLabel={clearLabel}
        id={id}
        max={max}
        min={min}
        onChange={(day) => onChange(!day || asDay ? day : day === value.slice(0, 10) ? value : `${day}T00:00:00.000000Z`)}
        placeholder={placeholder}
        value={value.slice(0, 10)}
      />
    </FieldShell>
  );
};
DateField.displayName = 'DateField';

interface TimeFieldProps {
  label: string;
  /** `HH:MM` */
  value: string;
  onChange: (value: string) => void;
  description?: string;
  hint?: string;
  error?: string;
}

export const TimeField = ({ label, value, onChange, description, hint, error }: TimeFieldProps) => {
  const id = useId();
  return (
    <FieldShell description={description} error={error} hint={hint} id={id} label={label}>
      <Input aria-invalid={Boolean(error)} id={id} onChange={(event) => onChange(event.target.value)} type="time" value={value} />
    </FieldShell>
  );
};
TimeField.displayName = 'TimeField';

interface DateTimeFieldProps {
  label: string;
  /** ISO UTC instant */
  value: string;
  onChange: (value: string) => void;
  description?: string;
  hint?: string;
  error?: string;
  /** First and last `YYYY-MM-DD` that may be picked */
  min?: string;
  max?: string;
}

/** A UTC instant edited as a calendar day and an `HH:MM` time. */
export const DateTimeField = ({ label, value, onChange, description, hint, error, min, max }: DateTimeFieldProps) => {
  const id = useId();
  const day = value.slice(0, 10);
  const time = value.slice(11, 16);
  return (
    <FieldShell description={description} error={error} hint={hint} id={id} label={label}>
      <div className="grid grid-cols-[minmax(0,1fr)_8rem] gap-2">
        <DatePicker aria-invalid={Boolean(error)} id={id} max={max} min={min} onChange={(next) => next && onChange(toUtcInstantIso(next, time))} value={day} />
        <Input
          aria-invalid={Boolean(error)}
          aria-label={`${label} time`}
          onChange={(event) => event.target.value && onChange(toUtcInstantIso(day, event.target.value))}
          type="time"
          value={time}
        />
      </div>
    </FieldShell>
  );
};
DateTimeField.displayName = 'DateTimeField';

interface CheckboxFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
  disabled?: boolean;
}

export const CheckboxField = ({ label, checked, onChange, description, disabled }: CheckboxFieldProps) => {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox checked={checked} disabled={disabled} id={id} onCheckedChange={(next) => onChange(next === true)} />
      <FieldLabel className={disabled ? 'text-muted-foreground' : undefined} description={description} htmlFor={id} label={label} />
    </div>
  );
};
CheckboxField.displayName = 'CheckboxField';
